import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import GuessTheYear from './GuessTheYear';
import OrderEventsQuiz from './OrderEventsQuiz';
import FillInTheBlank from './FillInTheBlank'; 
import WhichCameFirst from './WhichCameFirst';
import TrueOrFalse from './TrueOrFalse';
import MasterQuiz from './MasterQuiz';

const Quiz = ({ events }) => {
  const { t } = useTranslation();
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [started, setStarted] = useState(false);

  const quizModes = [
    { value: 'guessYear', label: t('quiz.modes.guessYear') },
    { value: 'orderEvents', label: t('quiz.modes.orderEvents') },
    { value: 'fillInBlank', label: t('quiz.modes.fillInBlank') },
    { value: 'whichCameFirst', label: t('quiz.modes.whichCameFirst') },
    { value: 'trueOrFalse', label: t('quiz.modes.trueOrFalse') },
    { value: 'masterQuiz', label: t('quiz.modes.masterQuiz') }
  ];

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        const res = await axios.get('https://amora-backend.onrender.com/api/student/decks');
        setDecks(res.data);
      } catch (err) {
        console.error('Failed to fetch decks:', err);
      }
    };
    fetchDecks();
  }, []);

  const getDeckEvents = () => {
    const deck = decks.find((d) => d.title === selectedDeck);
    return deck?.events || [];
  };

  const renderQuizMode = () => {
    const deckEvents = getDeckEvents();
    switch (selectedMode) {
      case 'guessYear':
        return <GuessTheYear events={deckEvents} />;
      case 'orderEvents':
        return <OrderEventsQuiz events={deckEvents} />;
      case 'fillInBlank':
        return <FillInTheBlank events={deckEvents} />;
      case 'whichCameFirst':
        return <WhichCameFirst events={deckEvents} onQuit={() => setStarted(false)} />;
      case 'trueOrFalse':
        return <TrueOrFalse events={deckEvents} onQuit={() => setStarted(false)} />;
      case 'masterQuiz':
        return <MasterQuiz events={deckEvents} onQuit={() => setStarted(false)} />;
      default:
        return null;
    }
  };

  if (!started) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-6 bg-gray-900 text-white rounded-lg shadow-lg border border-gray-800">
        <h2 className="text-3xl font-bold mb-6 text-purple-400 text-center">
          {t('quiz.heading')}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">{t('quiz.selectDeck')}</label>
            <select
              value={selectedDeck}
              onChange={(e) => setSelectedDeck(e.target.value)}
              className="w-full bg-gray-800 text-white border border-gray-600 rounded px-4 py-2"
            >
              <option value="">{t('quiz.chooseDeck')}</option>
              {decks.map((deck) => (
                <option key={deck._id} value={deck.title}>{deck.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">{t('quiz.selectMode')}</label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              className="w-full bg-gray-800 text-white border border-gray-600 rounded px-4 py-2"
            >
              <option value="">{t('quiz.chooseMode')}</option>
              {quizModes.map((mode) => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
          </div>

          <button
            disabled={!selectedDeck || !selectedMode}
            onClick={() => setStarted(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition disabled:opacity-50"
          >
            {t('quiz.start')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <button
        onClick={() => setStarted(false)}
        className="mb-4 text-sm text-blue-400 hover:underline"
      >
        ← {t('quiz.back')}
      </button>

      {renderQuizMode()}
    </div>
  );
};

export default Quiz;
